<script setup lang="ts">
    import {computed, ref, onMounted} from 'vue';
    import useCpBem from '~/composables/useCpBem';
    import CpSectionHeader from '~/components/cp/CpSection/CpSectionHeader/CpSectionHeader.vue';
    import CpButton from '~/components/cp/CpButton/CpButton.vue';
    import {trimHtmlToTextLength} from '~/utils/textLimit';
    import {useI18n} from 'vue-i18n';

    const props = withDefaults(defineProps<{
        body: string
        image?: string
        brandName?: string
    }>(), {
        brandName: '',
    });

    const {b, e} = useCpBem('cp-section-pdp-about-manufacturer');
    const {t} = useI18n();

    const MAX_CHARACTERS = 800;
    const showFullDescription = ref(false);
    // need to check due to SEO reasons
    const isSSR = ref(true);

    const descriptionCleaned = (body: string) => body
    .replace(/<[^>]*>?/gm, '')
    .replace(/&[a-zA-Z]acute;/g, ' ')
    .replace(/&nbsp;/g, '');

    const descriptionWithoutEmptyParagraphs = (body: string) => body.replaceAll(
        /<p>&nbsp;<\/p>|<br\s*\/?>/g,
        '',
    );

    const bodyFormat = computed(() => {
        if (!props.body) return '';
        if (isSSR.value) return props.body;
        return descriptionCleaned(props.body).length > MAX_CHARACTERS && !showFullDescription.value ?
                trimHtmlToTextLength(descriptionWithoutEmptyParagraphs(props.body), MAX_CHARACTERS) :
                descriptionWithoutEmptyParagraphs(props.body) || '';
    });

    onMounted(() => {
        isSSR.value = false;
    });
</script>

<template>
    <div :class="b">
        <CpSectionHeader type="h2" :title="t('aboutManufacturer')" :data-testid="e('title')" bg-color="white" />
        <div :class="e('main-container')">
            <img v-if="image" :src="image" :alt="`${brandName?.charAt(0).toUpperCase() + brandName?.slice(1)?.toLowerCase()} brand logo`" :class="e('image')">
            <div :class="e('text-container')">
                <div :class="e('body')" :data-testid="e('body')" v-html="bodyFormat" />
                <div v-if="!showFullDescription && descriptionCleaned(body).length > MAX_CHARACTERS" :class="e('gradient')" />
            </div>
            <CpButton
                v-if="descriptionCleaned(body).length > MAX_CHARACTERS"
                :class="e('button')"
                :label="showFullDescription ? 'Ver menos' : 'Ver más'"
                kind="iconlabel"
                :icon="showFullDescription ? 'expand_less' : 'expand_more'"
                full-width
                hollow
                without-border
                icon-size="small"
                @click="showFullDescription = !showFullDescription"
            />
        </div>
    </div>
</template>

<style scoped lang="scss">
    .cp-section-pdp-about-manufacturer {
        border-top: 1px solid var(--color-neutral-extra-light);
        border-bottom: 1px solid var(--color-neutral-extra-light);
        background: var(--color-canvas);

        &__body {
            color: var(--color-neutral-dark);
            text-decoration-color: var(--color-neutral-dark);
            font-family: var(--cp-font-family);
            font-weight: var(--cp-regular);
            font-size: var(--cp-body-regular-size);
            line-height: var(--cp-body-regular-line-height);

            &:deep(p) {
                margin-bottom: 16px;
            }

            &:deep(p:last-child) {
                margin-bottom: 0 !important;
            }

            &:deep(a) {
                color: var(--color-primary) !important;
                text-decoration: underline;
                font-weight: var(--cp-bold);
            }

            &:deep(ul) {
                padding: 16px;
            }

            &:deep(h2), &:deep(h3), &:deep(h1) {
                margin-bottom: 16px;
            }

            &:deep(ol) {
                padding: 16px;
            }
        }

        &__button {
            &:deep(.cp-text) {
                font-size: var(--cp-body-regular-size);;
            }
            max-height: 32px;
        }

        &__gradient {
            position: absolute;
            bottom: 0;
            left: 0;
            width: 100%;
            height: 50px;
            background: linear-gradient(180deg, rgba(250, 250, 250, 0.00) 0%, #FAFAFA 100%);
        }

        &__main-container {
            display: flex;
            flex-direction: column;
            padding: var(--cp-gutter);
            gap: var(--cp-gutter);
            align-items: center;
        }

        &__text-container {
            position: relative;
        }

        &__image {
            width: 160px;
            height: 72px;
            object-fit: contain;
            background-color: var(--color-white);
            border-radius: var(--cp-border-radius);
            padding: var(--cp-gutter-xsmall);
        }
    }
</style>

<i18n locale="es-US" lang="yaml">
  aboutManufacturer: "Acerca del Fabricante"
</i18n>
